// Saved-board data layer: the `GET /v1/dashboards` keyset loop + list query
// and the board mutations (create-with-template / duplicate / rename /
// patch-config / delete — toasts, `dashboards.all` invalidation, board
// navigation). Widget configs are persisted EXACTLY as authored: no time range
// and no ambient agent scope is ever baked into a stored widget (the range and
// `?agent=` apply at read time only).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { useToast } from "../ui";
import {
  canonicalWorkingScopeSearch,
  useResolvedWorkingTeamId,
  type WorkingScopeSearch,
} from "./workingScope";
import { useTypedNavigate } from "./typedRouter";
import { apiClient } from "./apiClient";
import {
  DASHBOARD_TEMPLATES,
  newWidgetId,
  type DashboardWidget,
} from "./dashboardWidgets";
import { ApiRequestError, requestErrorDescription } from "./errors";
import { MAX_PAGE_SIZE } from "./pagination";
import { queryKeys } from "./queryKeys";
import type { DashboardDetailSearch } from "./routerSearch";

type DashboardResponse = components["schemas"]["DashboardResponse"];

// The full search a dashboard route sees: its own validated params plus the
// root-owned `?agent` scope.
type DashboardSearchState = DashboardDetailSearch & WorkingScopeSearch;

const firstPageToken: string | undefined = undefined;

// The `$dashboardId` path param, readable from either dashboard route.
export function useDashboardParam(): string | undefined {
  const params = useParams({ strict: false });
  return "dashboardId" in params &&
    typeof params.dashboardId === "string" &&
    params.dashboardId.length > 0
    ? params.dashboardId
    : undefined;
}

// An agent-owned board pins its data to its owner; org boards follow the
// canonical selector — mirrors the server's `:render` rule.
export function dashboardWidgets(
  dashboard: DashboardResponse | undefined,
): DashboardWidget[] {
  return dashboard?.config?.widgets ?? [];
}

// Keyset loop: follow `next_page_token` until exhausted so the board select
// always has the complete list. Agent scope sends `agent_id`; All agents
// sends the working team's `team_id` (docs/09 §4 — org-wide boards still
// return under a team filter by backend design). Never both.
export interface FetchDashboardsScope {
  agentId?: string;
  teamId?: string;
}

export async function fetchDashboards(
  scope: FetchDashboardsScope,
): Promise<DashboardResponse[]> {
  const dashboards: DashboardResponse[] = [];
  let pageToken = firstPageToken;
  do {
    const { data, error } = await apiClient.GET("/v1/dashboards", {
      params: {
        query: {
          agent_id: scope.agentId,
          page_size: MAX_PAGE_SIZE,
          page_token: pageToken,
          team_id: scope.teamId,
        },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    dashboards.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return dashboards;
}

export interface CreateDashboardInput {
  displayName: string;
  templateId?: string;
  /** Owner agent: pins the board's data to this agent and lists it under
   * that agent's scope; undefined = organization-wide board. */
  agentId?: string;
}

export interface UseDashboardBoardsOptions {
  agentId?: string;
  dashboardId?: string;
}

export interface DashboardBoardsApi {
  createDashboard: UseMutationResult<
    DashboardResponse,
    Error,
    CreateDashboardInput
  >;
  dashboards: UseQueryResult<DashboardResponse[]>;
  deleteDashboard: UseMutationResult<void, Error, void>;
  duplicateDashboard: UseMutationResult<
    DashboardResponse,
    Error,
    DashboardResponse
  >;
  patchConfig: UseMutationResult<
    DashboardResponse,
    Error,
    readonly DashboardWidget[]
  >;
  renameDashboard: UseMutationResult<DashboardResponse, Error, string>;
}

// Board navigations carry canonical root scope and shared `?range`
// (URL-state law: board switches drop `?edit=` / `?widget=`). Selected-agent
// scope is agent-only; All agents keeps the team scope.
function carryScopeAndRange(prev: DashboardSearchState): DashboardSearchState {
  return { ...canonicalWorkingScopeSearch(prev), range: prev.range };
}

function templateWidgets(templateId: string | undefined): DashboardWidget[] {
  const template = DASHBOARD_TEMPLATES.find(
    (candidate) => candidate.id === templateId,
  );
  return template ? template.widgets() : [];
}

export function useDashboardBoards({
  agentId,
  dashboardId,
}: UseDashboardBoardsOptions): DashboardBoardsApi {
  const navigate = useTypedNavigate<
    DashboardSearchState,
    "/dashboard" | "/dashboard/$dashboardId",
    { dashboardId: string }
  >();
  const queryClient = useQueryClient();
  const toast = useToast();

  // All agents = the working team's boards (docs/09 §4); held until the shell
  // resolves the team. Agent wins: never both filters.
  const workingTeamId = useResolvedWorkingTeamId();
  const dashboards = useQuery({
    enabled: agentId != null || workingTeamId !== undefined,
    queryKey:
      agentId != null
        ? queryKeys.dashboards.list({ agent_id: agentId })
        : queryKeys.dashboards.list({ team_id: workingTeamId }),
    queryFn: () =>
      fetchDashboards(
        agentId != null ? { agentId } : { teamId: workingTeamId },
      ),
  });

  const invalidateDashboards = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.dashboards.all,
    });
  };

  const goToBoard = (boardId: string) => {
    void navigate({
      params: { dashboardId: boardId },
      search: carryScopeAndRange,
      to: "/dashboard/$dashboardId",
    });
  };

  const createDashboard = useMutation({
    mutationFn: async ({
      displayName,
      templateId,
      agentId: ownerAgentId,
    }: CreateDashboardInput) => {
      const { data, error } = await apiClient.POST("/v1/dashboards", {
        body: {
          agent_id: ownerAgentId ?? null,
          config: { widgets: templateWidgets(templateId) },
          display_name: displayName.trim(),
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (dashboard) => {
      toast.success("Dashboard created");
      await invalidateDashboards();
      goToBoard(dashboard.id);
    },
    onError: (error) => {
      toast.error("Dashboard create failed", {
        description: requestErrorDescription(
          error,
          "Dashboard request failed.",
        ),
      });
    },
  });

  const duplicateDashboard = useMutation({
    mutationFn: async (source: DashboardResponse) => {
      const widgets = dashboardWidgets(source).map((widget) => ({
        ...widget,
        id: newWidgetId(),
      }));
      const { data, error } = await apiClient.POST("/v1/dashboards", {
        body: {
          agent_id: source.agent_id ?? null,
          config: { widgets },
          display_name: `${source.display_name} copy`,
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (dashboard) => {
      toast.success("Dashboard duplicated");
      await invalidateDashboards();
      goToBoard(dashboard.id);
    },
    onError: (error) => {
      toast.error("Dashboard duplicate failed", {
        description: requestErrorDescription(
          error,
          "Dashboard request failed.",
        ),
      });
    },
  });

  const renameDashboard = useMutation({
    mutationFn: async (displayName: string) => {
      if (!dashboardId) {
        throw new Error("Dashboard id is required.");
      }
      const { data, error } = await apiClient.PATCH("/v1/dashboards/{id}", {
        body: {
          config: null,
          display_name: displayName.trim(),
        },
        params: { path: { id: dashboardId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async () => {
      toast.success("Dashboard renamed");
      await invalidateDashboards();
    },
    onError: (error) => {
      toast.error("Dashboard rename failed", {
        description: requestErrorDescription(
          error,
          "Dashboard request failed.",
        ),
      });
    },
  });

  // The one config write path: widget create/edit/duplicate/remove and layout
  // saves all PATCH the full widget list (the config is one document).
  const patchConfig = useMutation({
    mutationFn: async (widgets: readonly DashboardWidget[]) => {
      if (!dashboardId) {
        throw new Error("Dashboard id is required.");
      }
      const { data, error } = await apiClient.PATCH("/v1/dashboards/{id}", {
        body: {
          config: { widgets: [...widgets] },
          display_name: null,
        },
        params: { path: { id: dashboardId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async () => {
      await invalidateDashboards();
    },
    onError: (error) => {
      toast.error("Dashboard update failed", {
        description: requestErrorDescription(
          error,
          "Dashboard request failed.",
        ),
      });
    },
  });

  const deleteDashboard = useMutation({
    mutationFn: async () => {
      if (!dashboardId) {
        throw new Error("Dashboard id is required.");
      }
      const { error } = await apiClient.DELETE("/v1/dashboards/{id}", {
        params: { path: { id: dashboardId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Dashboard deleted");
      await invalidateDashboards();
      void navigate({
        search: carryScopeAndRange,
        to: "/dashboard",
      });
    },
    onError: (error) => {
      toast.error("Dashboard delete failed", {
        description: requestErrorDescription(
          error,
          "Dashboard request failed.",
        ),
      });
    },
  });

  return {
    createDashboard,
    dashboards,
    deleteDashboard,
    duplicateDashboard,
    patchConfig,
    renameDashboard,
  };
}
